import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const links = [
  { label: "Welcome", id: "hero" },
  { label: "Services", id: "services" },
  { label: "Schedule", id: "schedule" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[9000] transition-[background-color,border-color,padding] duration-300 ${
        scrolled
          ? "bg-[#050505]/90 backdrop-blur-md border-b border-white/10 py-4"
          : "bg-transparent border-b border-transparent py-6"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-12">
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo("hero")}
          className="group flex items-center gap-3"
        >
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_emergency-auto-6/artifacts/xqn88ffu_Storms%20Road%20Circle.png"
            alt="Storm's Roadside logo"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-white/15 transition-transform duration-300 group-hover:scale-105 md:h-12 md:w-12"
          />
          <span className="hidden font-display text-xl tracking-tight text-white transition-colors group-hover:text-primary sm:inline md:text-2xl">
            STORM<span className="text-primary">&apos;</span>S ROADSIDE
          </span>
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="group relative overline text-white/70 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <a
          data-testid="navbar-call-btn"
          href="tel:5044352757"
          className="group flex items-center gap-2 border border-primary bg-primary/0 px-4 py-2 text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-primary"
        >
          <Phone className="h-4 w-4 text-primary transition-colors group-hover:text-white" />
          <span className="hidden sm:inline">504-435-2757</span>
          <span className="sm:hidden">Call</span>
        </a>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          className="flex h-10 w-10 items-center justify-center border border-white/15 text-white md:hidden"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-md md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => {
                    setMenuOpen(false);
                    setTimeout(() => scrollTo(l.id), 380);
                  }}
                  className="border-b border-white/10 py-4 text-left font-display text-2xl text-white last:border-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
